import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTeacherProfile } from '../services/teachers';
import { TeacherProfile, VerificationStatus } from '../types/models';
import { initials } from '../utils/strings';

function VerificationBadge({ status }: { status: VerificationStatus }) {
  if (status === 'verified') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        Yoga Alliance Verified
      </span>
    );
  }
  if (status === 'provisional') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        Pending Verification
      </span>
    );
  }
  return null;
}

export const TeacherProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<TeacherProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!username) return;
    getTeacherProfile(username)
      .then(setProfile)
      .catch(err => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [username]);

  if (loading) return <div className="text-center py-20 text-gray-500">Loading…</div>;
  if (error) return <div className="max-w-2xl mx-auto px-4 py-8 text-red-600">{error}</div>;
  if (!profile) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <Link to="/classes" className="text-sm text-gray-500 hover:text-gray-700 mb-6 inline-block">
        ← Back to classes
      </Link>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xl font-bold flex-shrink-0">
            {initials(profile.name)}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{profile.name}</h1>
            <div className="mt-1">
              <VerificationBadge status={profile.verificationStatus} />
            </div>
          </div>
        </div>

        <div className="mt-4 flex gap-6 text-sm text-gray-500">
          {profile.yearsExperience !== undefined && profile.yearsExperience > 0 && (
            <span><span className="font-medium text-gray-700">{profile.yearsExperience}</span> yrs experience</span>
          )}
          <span><span className="font-medium text-gray-700">{profile.totalClassesLed}</span> classes led</span>
        </div>
      </div>

      {profile.bio && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">About</h2>
          <p className="text-gray-700 leading-relaxed">{profile.bio}</p>
        </div>
      )}

      {((profile.certifications?.length ?? 0) > 0 || (profile.specialties?.length ?? 0) > 0) && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          {(profile.certifications?.length ?? 0) > 0 && (
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Certifications</h2>
              <div className="flex flex-wrap gap-2">
                {profile.certifications!.map(c => (
                  <span key={c} className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(profile.specialties?.length ?? 0) > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Specialties</h2>
              <div className="flex flex-wrap gap-2">
                {profile.specialties!.map(s => (
                  <span key={s} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Upcoming Classes
        </h2>
        {profile.upcomingClasses.length === 0 ? (
          <p className="text-sm text-gray-500">No upcoming classes scheduled.</p>
        ) : (
          <ul className="space-y-3">
            {profile.upcomingClasses.map(c => {
              const d = new Date(c.startDateTime);
              return (
                <li key={c.eventId} className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{c.title}</p>
                    <p className="text-xs text-gray-500">
                      {d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {' · '}
                      {d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </p>
                  </div>
                  <Link
                    to={`/classes/${c.eventId}`}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap"
                  >
                    View →
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TeacherProfilePage;
